import { observer } from "mobx-react-lite";
import { useRef, useState } from "react";

import {
  DefaultButton,
  PrimaryButton,
  ScrollablePane,
  ScrollbarVisibility,
  SelectionMode,
  Stack
} from "@bps/fluent-ui";
import { Contact } from "@stores/practice/models/Contact.ts";
import { InfiniteScrollList } from "@ui-components/InfiniteScrollList/InfiniteScrollList.tsx";
import { Selection } from "@ui-components/ShimmeredDetailsList/Selection.ts";

import { ContactListProps } from "../../contacts-lists/ContactList.types.ts";
import { ContactListNotFound } from "../../contacts-lists/ContactListNotFound.tsx";
import { useContactListSearch } from "../../hooks/useContactListSearch.ts";

export const AdvancedContactSearchList: React.FC<
  ContactListProps & {
    onDismiss: () => void;

    onPassSelectionChange: (value?: string | undefined) => void;
  }
> = observer(({ columns, filter, onDismiss, onPassSelectionChange }) => {
  const [selected, setSelected] = useState<string>();

  const selection = useRef(
    new Selection({
      onSelectionChanged: async () => {
        const indexes = selection.current.getSelectedIndices();
        const itemKey = indexes[0];
        const item = selection.current.getItems()[itemKey] as Contact;

        if (item) {
          setSelected(item.id);
        } else {
          setSelected(undefined);
        }
      }
    })
  );

  const clearSelection = () => {
    const indexes = selection.current.getSelectedIndices();
    const itemKey = indexes[0];
    if (itemKey) {
      selection.current.setIndexSelected(itemKey, false, false);
      setSelected(undefined);
    } else {
      onDismiss();
    }
  };

  const passSelectionToForm = () => {
    onPassSelectionChange(selected);
    onDismiss();
  };

  const search = useContactListSearch(filter);

  return (
    <>
      <div
        style={{
          position: "relative",
          flexGrow: 1,
          margin: 16,
          height: "100%"
        }}
      >
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.always}>
          <InfiniteScrollList<Contact>
            setKey="id"
            selectionMode={SelectionMode.single}
            stickyHeader
            selection={selection.current}
            onRenderNoResults={() => <ContactListNotFound filter={filter} />}
            getItems={search}
            columns={columns}
            onRenderRow={(props, defaultRender) => {
              if (!props || !defaultRender) {
                return null;
              }
              return defaultRender({
                ...props,
                styles: {
                  root: {
                    height: 60
                  },
                  check: { height: 60 }
                }
              });
            }}
          />
        </ScrollablePane>
      </div>
      <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }}>
        <PrimaryButton disabled={!selected} onClick={passSelectionToForm}>
          Select contact
        </PrimaryButton>
        <DefaultButton onClick={clearSelection}>Cancel</DefaultButton>
      </Stack>
    </>
  );
});
